import React, { useEffect, useState } from "react";

export function toError(e: unknown): Error {
  if (e instanceof Error) {
    return e;
  } else {
    return new Error(String(e));
  }
}

export function useAsyncEffect(fn: (isActive: () => boolean) => Promise<void>, deps: React.DependencyList) {
  useEffect(() => {
    let active = true;
    fn(() => active);

    return () => {
      active = false;
    };
  }, deps);
}

export function useFetchData(url: string, initialData: any = null) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    async function run() {
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          throw new Error(
            `HTTP error status: ${res.status}, body: ${await res.text()}`,
          );
        }
        setData(await res.json());
      } catch (e) {
        // A newer request aborted this one; drop its result silently.
        if (controller.signal.aborted) return;
        setError(toError(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    run();

    // Cancel this fetch if the url changes or the component unmounts, so a stale
    // page load can't overwrite a newer one.
    return () => controller.abort();
  }, [url]);

  return { data, loading, error };
}

export function Fetcher(props: {
  url: string;
  children: (data: any) => React.ReactNode;
}) {
  const { data, loading, error } = useFetchData(props.url, []);
  if (loading) {
    return <p>Loading...</p>;
  } else if (error) {
    return <p style={{ color: "red" }}>Error: {error.message}</p>;
  }
  return props.children(data);
}

export class ErrorBoundary extends React.Component<{
  children?: React.ReactNode;
}, {
  hasError: boolean;
  error?: Error;
}> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  render() {
    if (this.state.hasError) {
      return <pre style={{color: "red"}}>{this.state.error.stack}</pre>
    }

    return this.props.children;
  }
}

type MutationState =
  | { phase: 'idle' }
  | { phase: 'saving' }
  | { phase: 'saved' }
  | { phase: 'error'; error: Error };

export function useSaver(url: string, method: string) {
  const [state, setState] = useState<MutationState>({ phase: 'idle' });

  const mutate = async (data: unknown) => {
    setState({ phase: 'saving' });
    try {
      await fetchOrThrow(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: await getCSRFToken()
        },
        body: JSON.stringify(data),
      });
      setState({ phase: 'saved' });
    } catch (e) {
      setState({ phase: 'error', error: toError(e) });
    }
  };

  return [mutate, state] as const;
}

export function SaveButton({
  saverState,
  onSave,
}: {
  saverState: MutationState;
  onSave: () => void;
}) {
  return (
    <>
      <button onClick={onSave} disabled={saverState.phase === 'saving'}>
        SAVE
      </button>
      {saverState.phase === 'saving' && <span> Saving...</span>}
      {saverState.phase === 'saved' && <span> Saved.</span>}
      {saverState.phase === 'error' && <span style={{ color: 'red' }}> Error: <pre>{saverState.error.message}</pre></span>}
    </>
  );
}

export async function fetchOrThrow(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);

  if (!res.ok) {
    let bodyText = "";
    try {
      bodyText = await res.text();
    } catch {
      // ignore body read errors
    }

    throw new Error(
      `HTTP ${res.status} ${res.statusText}${bodyText ? ` — ${bodyText}` : ""}`
    );
  }

  return res;
}

export function getCSRFToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  if (!match) throw new Error('CSRF token cookie not found');
  return decodeURIComponent(match[1]);
}

export const CSRF_HEADER_NAME = 'x-csrf-token';

/**
 * Reads the `next` post-billing-setup redirect destination from the URL.
 */
export function getSafeBillingNextParam(search: string = window.location.search): string | null {
  const raw = new URLSearchParams(search).get('next');
  if (raw && (raw === '/oauth/authorize' || raw.startsWith('/oauth/authorize?'))) {
    return raw;
  }
  return null;
}

function _tzOffset(tz: string, isoDate: string): string {
  const ref = new Date(`${isoDate}T12:00:00Z`);
  const parts = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(ref);
  const name = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT';
  return name === 'GMT' ? '+00:00' : name.slice(3);
}

function _subtractDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Builds timezone-aware ISO start/end strings for a lookback window ending now.
 * Pass lookbackDays=0 to get all time (start anchored to 2000-01-01).
 */
export function buildDateRange(lookbackDays: number): { start: string; end: string } {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const todayLocal = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());
  const startDate = lookbackDays > 0 ? _subtractDays(todayLocal, lookbackDays) : '2000-01-01';
  return {
    start: `${startDate}T00:00:00${_tzOffset(tz, startDate)}`,
    end: `${todayLocal}T23:59:59.999${_tzOffset(tz, todayLocal)}`,
  };
}